import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { AuthService } from '../services/auth.service';

const EXCLUDED_URLS = [
  '/auth/refresh',
  '/auth/login',
  's3.amazonaws.com',
  's3.us-east-1.amazonaws.com',
];

// URLs públicas que requieren token de prueba


/**
 * Interceptor que se encarga de:
 * 1. Verificar si el token está a punto de expirar o expiró antes de hacer la petición;
 *    si es así, refresca el token.
 * 2. Agregar el token (Bearer) en las cabeceras.
 * 3. Manejar errores 401, si ocurrieran.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  /**
   * Método principal que intercepta todas las peticiones HTTP
   * @param request Petición HTTP original
   * @param next Siguiente manejador en la cadena de interceptores
   * @returns Observable<HttpEvent<any>>
   */
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // Verificar si la URL debe excluirse
    if (this.isExcludedUrl(request)) {
      // No agregamos token ni hacemos verificación de expiración
      return next.handle(request);
    }



    // Obtenemos el token (Access Token JWT) del localStorage (o de donde lo guardes)
    let token = this.authService.getAccessToken();

    // Si no hay token, redirigimos a login y enviamos la petición sin cabecera Authorization
    if (!token) {
      this.router.navigate(['/auth/login']);
      return next.handle(request);
    }

    // Verificamos si el token está expirado o por expirar
    const isExpiringSoon = this.authService.isTokenExpiringSoon(token);
    if (isExpiringSoon) {
      // Refrescamos antes de enviar la petición y compartimos el resultado
      return this.authService.handleRefreshToken(token).pipe(
        switchMap((newToken: string) => {
          // Guardamos el nuevo token en localStorage (o donde proceda)
          this.authService.setAccessToken(newToken);
          token = newToken; // actualizamos la variable local

          // Ahora sí, clonamos la petición con el token nuevo
          const authReq = request.clone({
            headers: request.headers.set('Authorization', `Bearer ${token}`),
          });
          return next.handle(authReq);
        }),
        catchError((err) => {
          // Si falla el refresh, forzamos logout y redirigimos a login
          this.authService.logout();
          this.router.navigate(['/auth/login']);
          return throwError(() => err);
        }),
      );
    }

    // Si NO está próximo a expirar, simplemente enviamos la petición con el token actual
    const authReq = request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`),
    });

    // Continuamos con el flujo de la petición
    return next.handle(authReq).pipe(
      catchError((error) => {
        // Si el status es 401 (por ej., token inválido o error del backend)
        if (error instanceof HttpErrorResponse && error.status === 401) {
          // Reintento único: intentamos refrescar y reemitir la petición una vez
          const alreadyRetried = request.headers.has('X-Refresh-Attempted');
          if (!alreadyRetried) {
            return this.authService.handleRefreshToken(token!).pipe(
              switchMap((newToken: string) => {
                this.authService.setAccessToken(newToken);
                const retryReq = request.clone({
                  headers: request.headers
                    .set('Authorization', `Bearer ${newToken}`)
                    .set('X-Refresh-Attempted', 'true'),
                });
                return next.handle(retryReq);
              }),
              catchError((err) => {
                this.authService.logout();
                this.router.navigate(['/auth/login']);
                return throwError(() => err);
              }),
            );
          }
          this.authService.logout();
          this.router.navigate(['/auth/login']);
          return throwError(() => error);
        }
        return throwError(() => error);
      }),
    );
  }

  /**
   * Revisa si la petición actual coincide con una de las URLs excluidas.
   * @param request Petición HTTP
   */
  private isExcludedUrl(request: HttpRequest<any>): boolean {
    const isExcluded = EXCLUDED_URLS.some((excluded) =>
      request.url.includes(excluded),
    );

    return isExcluded;
  }


}
